export const DEFAULT_HOUR_RANGE = [0, 24]

export const REGULAR_WEEKDAYS = new Set([
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday'
])

/**
 * Returns only recurring weekday store hours (excludes day: 'irregular').
 */
export const filterRegularStoreHours = storeHours =>
    (storeHours || []).filter(sh => REGULAR_WEEKDAYS.has(sh.day))

/**
 * Parse the hour portion from an "HH:MM" time string.
 * Returns null for invalid input.
 */
export const parseTimeHour = time => {
    if (typeof time !== 'string') return null
    const hour = parseInt(time.split(':')[0], 10)
    return Number.isNaN(hour) ? null : hour
}

/**
 * Derive the hour range that covers a store's registered recurring business hours
 * across all days of the week (with ±1 padding).
 * Irregular (特別営業時間) entries are intentionally excluded.
 * Handles overnight hours (close < open) by treating close as next-day.
 * Returns null when no valid regular store hours are found.
 */
export const calculateStoreHourRange = storeHours => {
    const ranges = filterRegularStoreHours(storeHours)
        .map(sh => {
            const openHour = parseTimeHour(sh.open_time)
            const closeHour = parseTimeHour(sh.close_time)
            if (openHour === null || closeHour === null) return null
            // Overnight: treat close hour as next-day (e.g. 10:00-02:00 → 10..26)
            return [openHour, closeHour < openHour ? closeHour + 24 : closeHour]
        })
        .filter(Boolean)

    if (ranges.length === 0) return null
    return [
        Math.min(...ranges.map(([o]) => o)) - 1,
        Math.max(...ranges.map(([, c]) => c)) + 1
    ]
}

/**
 * Merge multiple [start, end] ranges into a single range covering the union.
 * Falls back to DEFAULT_HOUR_RANGE when no valid ranges are provided.
 */
export const combineHourRanges = (...ranges) => {
    const valid = ranges.filter(Boolean)
    if (valid.length === 0) return DEFAULT_HOUR_RANGE
    return [
        Math.min(...valid.map(([s]) => s)),
        Math.max(...valid.map(([, e]) => e))
    ]
}

const toDateString = date => {
    if (!date) return null
    if (typeof date.format === 'function') return date.format('YYYY-MM-DD')
    if (date instanceof Date) {
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, '0')
        const d = String(date.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
    }
    if (typeof date === 'string') return date
    return null
}

/**
 * Returns irregular store hours whose valid_from/valid_through overlaps
 * the given date range (inclusive). Skips entries missing either date field.
 */
export const getIrregularHoursInDateRange = (storeHours, startDate, endDate) => {
    const rangeStart = toDateString(startDate)
    const rangeEnd = toDateString(endDate)
    if (!rangeStart || !rangeEnd) return []

    return (storeHours || []).filter(sh => {
        if (sh.day !== 'irregular' || !sh.valid_from || !sh.valid_through) return false
        return sh.valid_from <= rangeEnd && sh.valid_through >= rangeStart
    })
}
